import React, { useEffect, useState } from 'react';
import { 
  Alert, 
  Button, 
  Tabs, 
  Tooltip 
} from 'antd';
import { useSearchParams } from 'react-router-dom';
import GroupsTab from './GroupsTab';
import { UserAddOutlined } from '@ant-design/icons';
import LevelTag from '../../layout/context/LevelTag';
import AddFromOrganisation from '../access/AddFromOrganisation';
import { useCapability } from '../../hooks/useSelfCapabilities';
import UserTable from '../user/dashboard/dashboard';
import InviteModal, { type InviteTarget } from '../invitations/InviteModal';
import { nodeSettings } from '../../../service/delegation.service';
import { useSelector } from 'react-redux';
import { selectTreeIndex } from '../../../redux/context/selectors';
import { accessLabel } from '../../layout/accessLabel';
import { useOrganisationAccess } from '../../hooks/useOrganisationAccess';

/** Fired on `window` after a user is added, so the sidebar's Users count refetches. */
export const CONSOLE_USERS_CHANGED = 'console-users-changed';

export type UserTarget = InviteTarget;

const LEVEL_WORDS = {
  organisation: 'Organisation',
  entity: 'Entity',
  workspace: 'Workspace'
} as const;

/**
 * The people page of a node, the same at organisation, entity and workspace level: a heading
 * with the level tag, an "Invite people" button (the shared InviteModal, always through the
 * organisation's invitation) and the accounts under this node. At workspace level the
 * button that brings people in is "Add from organisation".
 */
const NodeUsers: React.FC<{
  targets: UserTarget[];
  /** The console this page belongs to: the accounts table lists everyone under it. */
  level: 'organisation' | 'entity' | 'workspace';
  consoleNodeId: number;
}> = ({ targets, level, consoleNodeId }) => {
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') === 'groups' ? 'groups' : 'people';
  const [inviteOpen, setInviteOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const canManageMembers = useCapability('MEMBERSHIP', 'manage');
  const isWorkspace = level === 'workspace';
  const [refresh, setRefresh] = useState(0);
  const access = useOrganisationAccess(consoleNodeId);
  // An organisation console always manages its users; an entity console when users are
  // delegated to it, or when the caller can invite into the organisation above (its
  // SUPERADMIN/ADMIN, whom the server lets act under any entity).
  // Until the setting is known the form stays hidden, so it never flashes for a read-only view.
  const treeIndex = useSelector(selectTreeIndex);
  const label = accessLabel({ id: consoleNodeId, kind: level }, treeIndex);
  useEffect(() => {
    document.title = label;
  }, [label]);
  const [delegated, setDelegated] = useState(false);
  const [settingKnown, setSettingKnown] = useState(level !== 'entity');
  const canManage = level === 'organisation' || isWorkspace || delegated || (!access.loading && access.canInvite);
  const accessKnown = level !== 'entity' || (settingKnown && !access.loading);

  useEffect(() => {
    if (level !== 'entity') return;
    // Clear the previous entity's state first, so switching entities never shows its form or list.
    setDelegated(false);
    setSettingKnown(false);
    let stale = false;
    nodeSettings(consoleNodeId)
      .then((settings) => {
        if (stale) return;
        setDelegated(Boolean(settings.users?.delegated));
        setSettingKnown(true);
      })
      .catch(() => {
        if (!stale) setSettingKnown(true);
      });
    return () => {
      stale = true;
    };
  }, [level, consoleNodeId]);

  let peopleHint = ' This is a read-only list; changes are made by the organisation.';
  if (isWorkspace) peopleHint = ' Open a person to change their role or remove them from the workspace.';
  else if (canManage) peopleHint = ' Open a person to change their access, reset their sign-in factors or suspend the account.';

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-3">
        <h2 className="text-xl font-semibold m-0">{label}</h2>
        <LevelTag
          label={LEVEL_WORDS[level]}
          level={level}
        />
      </div>
      <Tabs
        activeKey={tab}
        onChange={(key) => setParams(key === 'groups' ? { 
          tab: 'groups' 
        } : {}, { 
          replace: true 
        })}
        items={[
          {
            key: 'people',
            label: 'People',
            children: (
              <div className="flex flex-col gap-4">
                {accessKnown && !canManage && <Alert
                  type="info"
                  showIcon
                  message="User management for this entity is handled by the organisation."
                />}
                {isWorkspace && canManageMembers && <div
                  className="flex items-center justify-end gap-3"
                >
                  <Button
                    type="primary"
                    icon={<UserAddOutlined />}
                    onClick={() => setAddOpen(true)}
                  >Add from organisation</Button>
                </div>}
                {canManage && !isWorkspace && <div
                  className="flex items-center justify-end gap-3"
                >
                  {!access.loading && !access.canInvite && <span 
                    className="text-sm text-gray-500"
                  >
          You do not hold the right to invite into this organisation.
                  </span>}
                  <Tooltip 
                    title={access.canInvite ? undefined : 'Invitations go through the organisation, and you cannot invite into it.'}
                  >
                    <Button
                      type="primary"
                      icon={<UserAddOutlined />}
                      disabled={!access.canInvite}
                      onClick={() => setInviteOpen(true)}
                    >Invite people</Button>
                  </Tooltip>
                </div>}
                <div>
                  <div className="font-medium text-black">Accounts</div>
                  <div className="text-sm text-gray-500 mb-2">
          Everyone with a membership in this {level}.
                    {peopleHint}
                  </div>
                  <UserTable
                    key={refresh}
                    nodeId={isWorkspace ? undefined : consoleNodeId}
                    level={isWorkspace ? undefined : level}
                    readOnly={isWorkspace ? !canManageMembers : !canManage}
                    canManageMembers={canManageMembers}
                    canManageInherited={!isWorkspace && access.canInvite}
                    organisationId={!isWorkspace && canManage && access.canInvite ? access.organisation?.id : undefined}
                    targets={targets}
                  />
                </div>
                {isWorkspace && <AddFromOrganisation
                  open={addOpen}
                  workspaceId={consoleNodeId}
                  workspaceName={targets[0]?.name ?? 'this workspace'}
                  onClose={() => setAddOpen(false)}
                  onAdded={() => setRefresh((n) => n + 1)}
                />}
                {!isWorkspace && <InviteModal
                  open={inviteOpen}
                  onClose={() => setInviteOpen(false)}
                  organisationId={access.organisation?.id}
                  targets={targets}
                  defaultNodeIds={[consoleNodeId]}
                  onInvited={() => {
                    setRefresh((n) => n + 1);
                    // The sidebar's Users count lives in the layout, which listens for this.
                    window.dispatchEvent(new CustomEvent(CONSOLE_USERS_CHANGED));
                  }}
                />}
              </div>
            )
          }, {
            key: 'groups',
            label: 'Groups',
            children: <GroupsTab nodeId={consoleNodeId} level={level} />
          }
        ]}
      />
    </div>
  );
};

export default NodeUsers;
